import React, { useState } from "react";
import cn from "classnames";
import styles from "./Stepper.module.sass";
import Icon from "../../../../../components/Icon";
import { notificationFolderIncomplete, getStudentById } from "../../../../../services/student.service";
import { generateDocuments, downloadContract } from "../../../../../services/contract.service";
import { validateCerfaFolder } from "../../../../../services/folder.service";
import { signCerfa, signConvention } from "../../../../../services/signature.service";
import { showSuccess, showError } from "../../../../../services/toast.service";
import { createStudentMemo } from "../../../../../services/studentMemo.service";
import { useAuth } from "../../../../../contexts/AuthContext";

const Stepper = ({ progression, folder, onFolderUpdate }) => {
  const { currentUser } = useAuth();
  const [isValidating, setIsValidating] = useState(false);
  
  // Fonction pour créer un mémo de relance depuis le Stepper
  const createRelanceMemoFromStepper = async (studentId) => {
    try {
      const currentDate = new Date().toLocaleDateString('fr-FR');
      const memoData = {
        student_id: studentId,
        topic: `Relance dossier - ${currentDate}`,
        content: JSON.stringify({
          blocks: [{
            key: 'relance-stepper',
            text: `Relance effectuée le ${currentDate} depuis le suivi de dossier pour faire avancer la progression.`,
            type: 'unstyled',
            depth: 0,
            inlineStyleRanges: [],
            entityRanges: [],
            data: {}
          }]
        }),
        created_by: currentUser?.id
      };

      await createStudentMemo(memoData);
    } catch (error) {
      console.error("Erreur lors de la création du mémo de relance:", error);
      // Ne pas faire échouer l'action principale si le mémo échoue
    }
  };
  
  const handleGenerateCerfa = async () => {
    try {
      if (!folder?.id) {
        showError("Aucun dossier sélectionné");
        return;
      }

      // Appeler la fonction de génération de documents avec le folderId
      const result = await generateDocuments(folder.id);
      
      if (result) {
        showSuccess("Documents générés avec succès");
        // Refresh du dossier parent
        if (onFolderUpdate) {
          await onFolderUpdate();
        }
      }
    } catch (error) {
      console.error("Erreur lors de la génération du CERFA:", error);
      showError("Erreur lors de la génération des documents");
    }
  };

  const handleRelanceEleve = async () => {
    try {
      // Récupérer les données de l'étudiant depuis le folder
      const studentId = folder?.expand?.student_id?.id || folder?.student_id;
      
      if (!studentId) {
        showError("Impossible de trouver l'étudiant associé à ce dossier");
        return;
      }

      // Récupérer les données complètes de l'étudiant pour obtenir le user_ref
      const student = await getStudentById(studentId);
      
      if (!student || !student.user_ref) {
        showError("Impossible de trouver la référence utilisateur de l'étudiant");
        return;
      }
      
      // Envoyer la notification de relance
      await notificationFolderIncomplete(student.user_ref);
      
      // Créer un mémo de relance automatiquement
      await createRelanceMemoFromStepper(student.id);
      
      showSuccess("Notification de relance envoyée à l'élève et mémo créé");
      
      // Refresh du dossier parent
      if (onFolderUpdate) {
        await onFolderUpdate();
      }
    } catch (error) {
      console.error("Erreur lors de l'envoi de la notification de relance:", error);
      showError("Impossible d'envoyer la notification de relance");
    }
  };

  const handleDownloadCerfa = async () => {
    try {
      // Récupérer le contrat associé au dossier
      const contract = folder?.expand?.contract_id || folder?.contract_id;
      
      if (!contract) {
        showError("Aucun contrat associé à ce dossier");
        return;
      }

      // Télécharger le document CERFA
      await downloadContract(contract);
      showSuccess("Téléchargement du CERFA en cours");
      
      // Refresh du dossier parent
      if (onFolderUpdate) {
        await onFolderUpdate();
      }
    } catch (error) {
      console.error("Erreur lors du téléchargement du CERFA:", error);
      showError("Impossible de télécharger le CERFA");
    }
  };

  const handleDownloadConvention = async () => {
    try {
      // Récupérer le contrat associé au dossier
      const contract = folder?.expand?.contract_id || folder?.contract_id;
      
      if (!contract) {
        showError("Aucun contrat associé à ce dossier");
        return;
      }

      // Télécharger la convention
      await downloadContract({ ...contract, fileField: 'convention' });
      showSuccess("Téléchargement de la convention en cours");
      
      // Refresh du dossier parent
      if (onFolderUpdate) {
        await onFolderUpdate();
      }
    } catch (error) {
      console.error("Erreur lors du téléchargement de la convention:", error);
      showError("Impossible de télécharger la convention");
    }
  };

  const handleValidateCerfa = async () => {
    try {
      if (!folder?.id) {
        showError("Aucun dossier sélectionné");
        return;
      }

      setIsValidating(true);

      // La fonction validateCerfaFolder gère maintenant automatiquement l'envoi à la signature
      const result = await validateCerfaFolder(folder.id);
      
      if (result) {
        showSuccess("CERFA validé et envoyé à la signature avec succès");
        
        // Refresh du dossier parent au lieu de recharger la page
        if (onFolderUpdate) {
          await onFolderUpdate();
        }
      }
    } catch (error) {
      console.error("Erreur lors de la validation du CERFA:", error);
      showError("Erreur lors de la validation du CERFA");
    } finally {
      setIsValidating(false);
    }
  };

  const handleSendDocumentsToSignature = async () => {
    try {
      // Récupérer le contrat associé au dossier
      const contract = folder?.expand?.contract_id || folder?.contract_id;
      
      if (!contract) {
        showError("Aucun contrat associé à ce dossier");
        return;
      }

      // Vérifier que les documents existent
      if (!contract.document) {
        showError("Aucun document CERFA disponible");
        return;
      }

      if (!contract.convention) {
        showError("Aucune convention disponible");
        return;
      }

      // Vérifier les emails requis
      const student = contract.expand?.student_id || {};
      const company = contract.expand?.company_id || {};
      const center = contract.expand?.center_id || {};
      
      const missingEmails = [];
      
      if (!company.email && !company.contact_email && !company.director_email) {
        missingEmails.push("l'entreprise");
      }
      if (!student.email) {
        missingEmails.push("l'étudiant");
      }
      if (!center.email && !center.contact_email && !center.director_email) {
        missingEmails.push("le centre de formation");
      }
      
      if (missingEmails.length > 0) {
        showError(`Emails manquants pour ${missingEmails.join(', ')}`);
        return;
      }

      // Envoyer le CERFA à la signature
      const cerfaResult = await signCerfa(folder.id);
      
      if (cerfaResult) {
        showSuccess("CERFA envoyé à la signature avec succès");
      }

      // Envoyer la Convention à la signature
      const conventionResult = await signConvention(contract.id);
      
      if (conventionResult) {
        showSuccess("Convention envoyée à la signature avec succès");
      }
      
      // Refresh du dossier parent après envoi des signatures
      if (onFolderUpdate && (cerfaResult || conventionResult)) {
        await onFolderUpdate();
      }
      
    } catch (error) {
      console.error("Erreur lors de l'envoi des documents à la signature:", error);
      showError("Erreur lors de l'envoi des documents à la signature");
    }
  };

  // Logique pour déterminer l'étape actuelle
  const getCurrentStep = () => {
    const etudiant = progression?.etudiant || {};
    const entreprise = progression?.entreprise || {};
    const ecole = progression?.ecole || {};
    
    // Étape 1: Informations élève
    if (!etudiant.infos_remplies) return 1;
    
    // Vérifier si une entreprise est associée
    const hasCompany = folder?.expand?.company_id;
    if (!hasCompany) return 1.5;
    
    // Étape 2: Informations entreprise
    if (!entreprise.infos_remplies || !entreprise.contrat_cree || !entreprise.tuteur_assigne) {
      return 2;
    }
    
    // Étape 3: Génération documents (école)
    if (!ecole.cerfa_genere) return 3;
    
    // Étape 4: Validation par l'école
    if (!ecole.valide_cerfa) return 4;
    
    // Étape 5: Signatures par tous les acteurs
    // Vérifier si CERFA (3 signatures) et Convention (2 signatures) sont complètes
    const cerfaCompleted = etudiant.sign_cerfa && entreprise.sign_cerfa && ecole.sign_cerfa;
    const conventionCompleted = entreprise.sign_convention && ecole.sign_convention;
    
    if (!cerfaCompleted || !conventionCompleted) return 5;
    
    // Étape 6: Dossier terminé - prêt pour envoi OPCO
    return 6;
  };

  const currentStep = getCurrentStep();

  const hasCompany = folder?.expand?.company_id;

  // Fonction pour compter les signatures CERFA
  const getCerfaSignatureCount = () => {
    const etudiant = progression?.etudiant || {};
    const entreprise = progression?.entreprise || {};
    const ecole = progression?.ecole || {};
    
    let count = 0;
    if (etudiant.sign_cerfa) count++;
    if (entreprise.sign_cerfa) count++;
    if (ecole.sign_cerfa) count++;
    
    return count;
  };

  // Fonction pour compter les signatures Convention
  const getConventionSignatureCount = () => {
    const entreprise = progression?.entreprise || {};
    const ecole = progression?.ecole || {};
    
    let count = 0;
    if (entreprise.sign_convention) count++;
    if (ecole.sign_convention) count++;
    
    return count; // Convention = 2 signataires (école + entreprise)
  };

  // Fonction pour obtenir le statut global des signatures
  const getGlobalSignatureStatus = () => {
    const cerfaCount = getCerfaSignatureCount();
    const conventionCount = getConventionSignatureCount();
    
    // CERFA doit être signé par 3 parties, Convention par 2 parties
    const cerfaCompleted = cerfaCount === 3;
    const conventionCompleted = conventionCount === 2;
    
    return {
      cerfaCount,
      conventionCount,
      cerfaCompleted,
      conventionCompleted,
      allCompleted: cerfaCompleted && conventionCompleted
    };
  };

  const steps = [
    {
      id: 1,
      title: "Informations élève",
      description: "L'élève doit compléter ses informations personnelles",
      status: progression?.etudiant?.infos_remplies ? "completed" : (currentStep === 1 ? "current" : "pending"),
      showButton: currentStep === 1 && !progression?.etudiant?.infos_remplies
    },
    // Afficher l'étape 1.5 uniquement si aucune entreprise n'est assignée
    ...(hasCompany ? [] : [{
      id: 1.5,
      title: "Assignation entreprise",
      description: "Une entreprise doit être assignée au dossier",
      status: currentStep === 1.5 ? "current" : "pending"
    }]),
    {
      id: 2,
      title: "Informations entreprise",
      description: "L'entreprise doit compléter toutes les informations requises",
      status: (() => {
        const ent = progression?.entreprise || {};
        if (ent.infos_remplies && ent.contrat_cree && ent.tuteur_assigne) {
          return "completed";
        }
        return currentStep === 2 ? "current" : "pending";
      })()
    },
    {
      id: 3,
      title: "Génération documents",
      description: "Le CFA génère les documents (CERFA, Convention)",
      status: progression?.ecole?.cerfa_genere ? "completed" : (currentStep === 3 ? "current" : "pending"),
      showButton: currentStep === 3 && !progression?.ecole?.cerfa_genere
    },
    {
      id: 4,
      title: "Validation par l'école",
      description: "Le CFA valide le dossier complet",
      status: (() => {
        const ecole = progression?.ecole || {};
        if (ecole.valide_cerfa) {
          return "completed";
        }
        return currentStep === 4 ? "current" : "pending";
      })(),
      showButton: currentStep === 4 && !progression?.ecole?.valide_cerfa,
      showDownload: currentStep >= 4 && progression?.ecole?.cerfa_genere
    },
    {
      id: 5,
      title: "Signatures des documents",
      description: (() => {
        const signatureStatus = getGlobalSignatureStatus();
        const cerfaText = `CERFA (${signatureStatus.cerfaCount}/3)`;
        const conventionText = `Convention (${signatureStatus.conventionCount}/2)`;
        
        return `${cerfaText} • ${conventionText}`;
      })(),
      status: (() => {
        const signatureStatus = getGlobalSignatureStatus();
        
        if (signatureStatus.allCompleted) {
          return "completed";
        }
        return currentStep === 5 ? "current" : "pending";
      })()
    },
    {
      id: 6,
      title: "Prêt pour envoi OPCO",
      description: "Le dossier est complet et peut être envoyé aux OPCO",
      status: currentStep === 6 ? "completed" : "pending"
    }
  ];

  return (
    <div className={styles.stepper}>
      
      <div className={styles.steps}>
        {steps.map((step, index) => (
          <div
            key={step.id}
            className={cn(styles.step, {
              [styles.completed]: step.status === "completed",
              [styles.current]: step.status === "current",
              [styles.pending]: step.status === "pending"
            })}
          >
            <div className={styles.stepIcon}>
              {step.status === "completed" ? (
                <Icon name="check" />
              ) : (
                <span className={styles.stepNumber}>
                  {step.id === 1.5 ? "1.5" : Math.floor(step.id)}
                </span>
              )}
            </div>
            
            <div className={styles.stepContent}>
              <div className={styles.stepTitle}>{step.title}</div>
              <div className={styles.stepDescription}>{step.description}</div>
              {step.showButton && (
                <button 
                  className={styles.generateButton}
                  onClick={
                    step.id === 1 ? handleRelanceEleve : 
                    step.id === 3 ? handleGenerateCerfa : 
                    step.id === 4 ? handleValidateCerfa : 
                    () => {}
                  }
                  disabled={step.id === 4 && isValidating}
                >
                  {step.id === 4 && isValidating ? (
                    <span className={styles.spinner}></span>
                  ) : (
                    step.id === 1 ? "Relancer l'élève" : 
                    step.id === 3 ? "Générer le CERFA" : 
                    step.id === 4 ? "Valider le dossier" : 
                    "Action"
                  )}
                </button>
              )}
              {step.showDownload && (
                <div className={styles.downloadButtons}>
                  <button 
                    className={styles.downloadButton}
                    onClick={handleDownloadCerfa}
                  >
                    <Icon name="download" size="16" />
                    Télécharger le CERFA
                  </button>
                  <button 
                    className={styles.downloadButton}
                    onClick={handleDownloadConvention}
                  >
                    <Icon name="download" size="16" />
                    Télécharger la convention
                  </button>
                </div>
              )}
            </div>
            
            {index < steps.length - 1 && (
              <div className={cn(styles.connector, {
                [styles.completed]: steps[index + 1].status === "completed" || steps[index + 1].status === "current"
              })} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Stepper;
